import { ChangeEvent, FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Divider,
  forwardRef,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { RiSearchFill } from 'react-icons/ri';
import { FaFilter, FaMapMarkedAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

import { Button, IconButton } from 'components';
import { resolvePath } from 'utils/helpers';
import { Paths } from 'utils/paths';
import { useResponsiveness } from 'hooks/useResponsiveness';
import { usePageTextContent } from 'context/pageTextContent';
import { useSearchPageStore } from '../../useSearchPageStore';

export const Control = forwardRef<object, 'form'>((_, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const near = useSearchPageStore(state => state.near);
  const onNearChange = useSearchPageStore(state => state.onNearChange);
  const toggleMap = useSearchPageStore(state => state.toggleMap);
  const onOpenFiltersModal = useSearchPageStore(
    state => state.onOpenFiltersModal,
  );

  const { isMobile } = useResponsiveness();

  const { content } = usePageTextContent<SearchPageContent>();

  const { searchFieldPlaceholder, mapButton } = content;

  const router = useRouter();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNearChange(event.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault();

    const pageUrl = resolvePath({
      pathname: Paths.SEARCH,
      query: { near },
    });

    router.push(pageUrl);
  };

  const onFiltersClick = () => {
    onOpenFiltersModal();
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onMapClick = () => {
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      return;
    }

    toggleMap();
  };

  return (
    <Stack
      direction="row"
      align="center"
      ref={ref}
      as="form"
      w="full"
      justify={['center', 'center', 'center']}
      onSubmit={onSubmit}
      px={[0, 0, '1rem']}
      spacing={['2.5rem', '2.5rem', '1rem']}
    >
      <InputGroup
        maxW={['unset', 'unset', '16.75rem']}
        minW="18rem"
        h="2.625rem"
        boxShadow="0px 5px 15px #00000029"
        rounded="lg"
        as={motion.div}
        animate={isFocused ? { scale: 1.025 } : {}}
      >
        <Input
          onFocus={onFocus}
          onBlur={onBlur}
          bg="white"
          h="full"
          onChange={onChange}
          value={near}
          placeholder={searchFieldPlaceholder}
          _focus={{ boxShadow: 'none' }}
          border="none"
          _placeholder={{
            color: 'gray.550',
            fontWeight: 300,
          }}
        />

        <InputRightElement h="full" w="auto" right={0}>
          <IconButton
            aria-label="Search"
            variant="ghost"
            _hover={{ bg: 'transparent' }}
            type="submit"
          >
            <Icon as={RiSearchFill} color="zmyle.primary" fontSize="1.25rem" />
          </IconButton>

          <Divider orientation="vertical" h="calc(100% - 1rem)" />

          <IconButton
            aria-label="Open filters"
            onClick={onFiltersClick}
            h="full"
            variant="ghost"
            bg="white"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
          >
            <Icon as={FaFilter} fontSize="1rem" />
          </IconButton>
        </InputRightElement>
      </InputGroup>

      <Button
        onClick={onMapClick}
        variant="ghost"
        color="blue.800"
        _hover={{ bg: 'transparent', color: 'zmyle.primary' }}
        px={0}
      >
        <HStack spacing="0.5rem">
          <Icon as={FaMapMarkedAlt} fontSize="1.25rem" />

          <Text overflow="hidden">{mapButton}</Text>
        </HStack>
      </Button>
    </Stack>
  );
});
